import Promise from 'bluebird';
import { handleActions } from 'redux-actions';
import http from '../shared/http';
import intl from '../shared/intl';
import endpoints from '../../config/endpoints';
import qs from 'qs';

import { createSelectOptionsFromSysEnums } from '../shared/form-elements/multiselect';
import { SubmissionError, getFormValues, destroy } from 'redux-form';
import { LOCATION_CHANGE } from 'react-router-redux';

import find from 'lodash/find';
import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import isNull from 'lodash/isNull';

const FETCH_DATA_SUCCESS = 'affiliates/fetch-data-success';
const FETCH_DATA_ERROR = 'affiliates/fetch-data-error';

const FORM_SUBMIT_SUCCESS = 'affiliates/submit-success';
const FORM_SUBMIT_ERROR = 'affiliates/submit-error';

const SHOW_AFFILIATE_OFFERS = 'affiliates/show-offers';
const HIDE_AFFILIATE_OFFERS = 'affiliates/hide-offers';

const SHOW_OFFER_TARGETS = 'affiliates/show-targets';
const HIDE_OFFER_TARGETS = 'affiliates/hide-targets';

const FETCH_AFFILIATE_OFFERS_SUCCESS = 'affiliates/fetch-affiliate-offers-success';
const FETCH_AFFILIATE_OFFERS_ERROR = 'affiliates/fetch-affiliate-offers-error';

const UPDATE_SHOWN_TARGETS_AMOUNT = 'affiliates/update-shown-targets-amount';
const TOGGLE_PROGRESS_BAR = 'affiliates/toggle-progress-bar';

const FETCH_AFFILIATE_BONUS_SUCCESS = 'affiliates/fetch-aff-bonus-success';
const ADD_OFFER_SUCCESS = 'affiliates/add-offer-success';
const CLICKED_ADD_OFFER = 'affiliates/clicked-add-offer';

const APPLY = 'affiliates/apply';
const RESET_QUERY = 'affiliates/reset-query';

const COPY_TERMS_SUCCESS = 'affiliates/copy-terms-success';
const COPY_TERMS_ERROR = 'affiliates/copy-terms-error';

const NULLIFY_BOOST = 'affiliates/nullify-boost';

const initialState = {
	data: null,
	error: null,
	listOfActiveAffiliatesPanIndex: [],
	listOfAddedOffersPanIndex: [],
	listOfActiveOffersPanIndex: [],
	masterOffers: [],
	shouldShowAddAffiliateSelect: false,
	shownTargetPixelHeightsMap: {},
	field: {
		location_id: null,
		currency: null,
		amount: null,
		status: 'active'
	},
	selectOptions: {
		affiliates: [],
		offers: []
	},
	nullifiedBoosts: [],
	searchFormInitialValue: '',
	query: {
		search: '',
		offset: 0,
		limit: 20
	},
	progressBar: false
};

export const actions = {
	fetchData: (props, fetchOptions = {}, query = {}) => (dispatch, getState) => {
		// first request, destroy redux form state
		if (props) {
			dispatch(destroy('childAffiliatesForm'));
		}
		return requestData(dispatch, fetchOptions, query).then(action => {
			if (action.type === FETCH_DATA_SUCCESS) {
				return Promise.map(
					action.payload[0].data.rows,
					affiliate => {
						const request =
							affiliate.offer_ids &&
							affiliate.offer_ids.filter(Boolean).length &&
							http({
								url: `${endpoints.MASTER_ACCOUNT.BASE}/bonus/${affiliate.id}`,
								method: 'get',
								actions: [FETCH_AFFILIATE_BONUS_SUCCESS, FETCH_DATA_ERROR],
								addToResponse: {
									affiliate_id: affiliate.id
								}
							});
						return request ? request(dispatch) : { meta: { affiliate_id: affiliate.id } };
					},
					{ concurrency: 2 }
				);
			}
		});
	},

	submitForm: formData => (dispatch, getState) => {
		const state = getState();
		const translate = intl(state);
		const { field } = state.affiliates;
		const currencies = state.app.lists['available-currencies'];

		const dirtyData = formData.child_affiliates.map(aff => {
			return { affiliate_id: aff.affiliate.value, data: aff.offers };
		});

		if (dirtyData.length === 0) return;

		const affiliatesToSubmit = flatten(
			dirtyData.map(affiliate => {
				const res =
					affiliate &&
					affiliate.data.map(entity => {
						const fields = entity.targets.map(formField => {
							const correctFields = currencies.filter(currency => !!formField[currency.id]).map(currency => ({
								...field,
								...formField,
								status: 'active',
								location_id: formField['location_id'] ? formField['location_id']['value'] : '',
								currency: currency.id,
								amount: formField[currency.id]
							}));

							return correctFields.length
								? correctFields
								: {
									...field
								};
						});

						return {
							affiliateId: affiliate.affiliate_id,
							offer: (entity.offer && entity.offer.value) || null,
							fields: flatten(fields).filter(entity => !!entity.currency)
						};
					});

				return res.filter(entity => entity && !isEmpty(entity.fields));
			})
		);

		const urls = affiliatesToSubmit.map(entity =>
			`${endpoints.MASTER_ACCOUNT.INDIVIDUAL_TERMS}`
				.replace('{offerId}', entity.offer)
				.replace('{affiliateId}', entity.affiliateId)
		);
		const dataToSubmit = affiliatesToSubmit.map(entity => entity.fields);

		if (!urls || !urls.length || !dataToSubmit || !dataToSubmit.length) return;

		const request = http({
			url: urls,
			method: 'put',
			actions: [FORM_SUBMIT_SUCCESS, FORM_SUBMIT_ERROR],
			data: dataToSubmit
		});

		dispatch(actions.toggleProgressBar());

		return request(dispatch).then(action => {
			if (action.type === FORM_SUBMIT_SUCCESS) {
				dispatch(actions.resetForm());
				return requestData(dispatch, { shouldShowProgressBar: true })
					.then(action => {
						return Promise.map(
							affiliatesToSubmit,
							aff => dispatch(actions.fetchAffiliateOffers(null, { value: aff.affiliateId }, false)),
							{ concurrency: 2 }
						);
					})
					.then(() => dispatch(actions.toggleProgressBar()));
			}

			if (action.type === FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
				dispatch(actions.toggleProgressBar());
				const validationsErrors = prepareValidationError(formData, translate);
				throw new SubmissionError({
					child_affiliates: validationsErrors
				});
			}
		});
	},

	showAffiliateOffers: index => ({
		type: SHOW_AFFILIATE_OFFERS,
		payload: index
	}),

	hideAffiliateOffers: index => ({
		type: HIDE_AFFILIATE_OFFERS,
		payload: index
	}),

	showOfferTargets: (index, parentIndex) => ({
		type: SHOW_OFFER_TARGETS,
		payload: { index, parentIndex }
	}),

	hideOfferTargets: (index, parentIndex) => ({
		type: HIDE_OFFER_TARGETS,
		payload: { index, parentIndex }
	}),

	fetchAffiliateOffers: (index, affiliate, shouldShowProgressBar = true) => (dispatch, getState) => {
		if (!affiliate || !affiliate.value) return null;

		const state = getState();
		const { data } = state.affiliates;
		const childAffiliateToFetch = find(data, { id: affiliate.value });

		if (
			!childAffiliateToFetch ||
			!Array.isArray(childAffiliateToFetch.offer_ids) ||
			!childAffiliateToFetch.offer_ids.filter(Boolean).length
		) {
			// dont make unnecessary requests if affiliate has no offers
			// index can be null if we do this stuff after submit
			return !isNull(index) && dispatch(actions.showAffiliateOffers(index));
		}

		const request = http({
			url: [
				`${endpoints.OFFERS.LIST}?affiliate_id=${affiliate.value}`,
				`${endpoints.MASTER_ACCOUNT.BASE}/bonus/${affiliate.value}`
			],
			method: 'get',
			actions: [FETCH_AFFILIATE_OFFERS_SUCCESS, FETCH_AFFILIATE_OFFERS_ERROR],
			addToResponse: {
				index,
				affiliate
			}
		});

		shouldShowProgressBar && dispatch(actions.toggleProgressBar());

		return request(dispatch).then(action => {
			shouldShowProgressBar && dispatch(actions.toggleProgressBar());
			if (action.type === FETCH_AFFILIATE_OFFERS_SUCCESS) {
				!isNull(index) && dispatch(actions.showAffiliateOffers(index));
			}
		});
	},

	updateShownTargetsPixelHeights: (index, amount) => ({
		type: UPDATE_SHOWN_TARGETS_AMOUNT,
		payload: { index, amount }
	}),

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	}),

	applySearch: () => (dispatch, getState) => {
		const searchFormValues = getFormValues('affiliatesSearchForm')(getState());
		const search = searchFormValues ? searchFormValues.search : '';
		dispatch(actions.toggleProgressBar());
		dispatch(actions.resetForm());
		dispatch(actions.fetchData(undefined, undefined, { search }));
	},

	addOffer: (offer, affiliate) => (dispatch, getState) => {
		const offerId = offer.value;
		const affiliateId = affiliate.value;

		const request = http({
			url: `${endpoints.MASTER_ACCOUNT.BASE}/add-offer`,
			method: 'post',
			data: {
				offer_id: offerId,
				affiliate_id: affiliateId
			},
			actions: [ADD_OFFER_SUCCESS, FETCH_DATA_ERROR]
		});
		dispatch(actions.toggleProgressBar());
		dispatch(actions.resetForm());
		dispatch(request).then(action => {
			if (action.type === ADD_OFFER_SUCCESS) {
				dispatch(actions.fetchData());
			}
		});
	},

	clickAddOffer: index => ({
		type: CLICKED_ADD_OFFER,
		payload: index
	}),

	showMore: () => (dispatch, getState) => {
		const { query } = getState().affiliates;
		const newQuery = { ...query, offset: query.offset + query.limit };
		const fetchOptions = {
			shouldShowProgressBar: false,
			shouldAppendData: true
		};

		dispatch({
			type: APPLY,
			payload: newQuery
		});

		dispatch(actions.toggleProgressBar());
		dispatch(actions.fetchData(null, fetchOptions, newQuery));
	},

	copyTerms: (affiliate, offer) => (dispatch, getState) => {
		const affiliateId = affiliate.value;
		const offerId = offer && offer.value;

		const data = {
			affiliate_id: affiliateId
		};

		if (offerId) {
			data.offer_id = offerId;
		}

		const request = http({
			url: `${endpoints.MASTER_ACCOUNT.BASE}/copy-terms`,
			method: 'put',
			data,
			actions: [COPY_TERMS_SUCCESS, COPY_TERMS_ERROR]
		});

		dispatch(actions.toggleProgressBar());
		dispatch(actions.resetForm());
		dispatch(request).then(action => {
			if (action.type === COPY_TERMS_SUCCESS) {
				dispatch(actions.fetchData());
			}
		});
	},

	nullifyBoostOnSubmit: target => ({
		type: NULLIFY_BOOST,
		payload: target
	}),

	resetForm: () => dispatch => {
		dispatch(destroy('childAffiliatesForm'));
		dispatch({
			type: RESET_QUERY
		});
	}

};

const reducers = {
	[LOCATION_CHANGE]: () => ({
		...initialState
	}),
	[FETCH_DATA_SUCCESS]: (state, action) => ({
		...state,
		data: action.meta.shouldAppendData
			? [].concat(state.data, action.payload[0].data.rows)
			: action.payload[0].data.rows,
		affiliatesTotal: action.payload[0].data.total,
		masterOffers: action.meta.shouldAppendData ? state.masterOffers : action.payload[1].data.filter(o => o.available),
		selectOptions: {
			...state.selectOptions,
			affiliates: createSelectOptionsFromSysEnums(
				action.payload[0].data.rows.map(aff => ({ id: aff.id, text: aff.email }))
			)
		},
		progressBar: action.meta && action.meta.shouldShowProgressBar ? action.meta.shouldShowProgressBar : false,
		error: false
	}),
	[FETCH_AFFILIATE_BONUS_SUCCESS]: (state, action) => ({
		...state,
		data: state.data.map(aff => {
			if (aff.id === action.meta.affiliate_id) {
				let usdRevenue = action.payload.data
					.filter(item => item.currency === 'usd')
					.map(item => item.revenue)
					.reduce((acc, curr) => (acc += parseFloat(curr)), 0);
				let rubRevenue = action.payload.data
					.filter(item => item.currency === 'rub')
					.map(item => item.revenue)
					.reduce((acc, curr) => (acc += parseFloat(curr)), 0);
				let eurRevenue = action.payload.data
					.filter(item => item.currency === 'eur')
					.map(item => item.revenue)
					.reduce((acc, curr) => (acc += parseFloat(curr)), 0);
				return { ...aff, usdRevenue, rubRevenue, eurRevenue };
			}
			return aff;
		})
	}),
	[FORM_SUBMIT_SUCCESS]: state => ({
		...state,
		listOfAddedOffersPanIndex: []
	}),
	[SHOW_AFFILIATE_OFFERS]: (state, action) => ({
		...state,
		listOfActiveAffiliatesPanIndex: [].concat(state.listOfActiveAffiliatesPanIndex, action.payload)
	}),

	[HIDE_AFFILIATE_OFFERS]: (state, action) => ({
		...state,
		listOfActiveAffiliatesPanIndex: state.listOfActiveAffiliatesPanIndex.filter(index => index !== action.payload)
	}),

	[CLICKED_ADD_OFFER]: (state, action) => ({
		...state,
		listOfAddedOffersPanIndex: [].concat(state.listOfAddedOffersPanIndex, action.payload)
	}),

	[SHOW_OFFER_TARGETS]: (state, action) => ({
		...state,
		listOfActiveOffersPanIndex: {
			...state.listOfActiveOffersPanIndex,
			[action.payload.parentIndex]: [].concat(
				state.listOfActiveOffersPanIndex[action.payload.parentIndex] || [],
				action.payload.index
			)
		}
	}),

	[HIDE_OFFER_TARGETS]: (state, action) => ({
		...state,
		listOfActiveOffersPanIndex: {
			...state.listOfActiveOffersPanIndex,
			[action.payload.parentIndex]: state.listOfActiveOffersPanIndex[action.payload.parentIndex].filter(
				index => index !== action.payload.index
			)
		}
	}),

	[UPDATE_SHOWN_TARGETS_AMOUNT]: (state, action) => ({
		...state,
		shownTargetPixelHeightsMap: {
			...state.shownTargetPixelHeightsMap,
			[action.payload.index]: (state.shownTargetPixelHeightsMap[action.payload.index] || 0) + action.payload.amount
		}
	}),

	[FETCH_AFFILIATE_OFFERS_SUCCESS]: (state, action) => ({
		...state,
		data: state.data.map(affiliate => {
			if (affiliate.id === action.meta.affiliate.value) {
				const offers = action.payload[0].data;
				const revenue = action.payload[1].data;
				const userOffers = offers.filter(o => affiliate.offer_ids.indexOf(o.id) !== -1).map(offer => {
					const revenuePerOffer = revenue.filter(rev => rev.offer_id === offer.id);
					return {
						...offer,
						revenue: revenuePerOffer
					};
				});
				const userOfferTerms = userOffers
					.map(offer => {
						return (
							offer.payouts &&
							offer.payouts.map(payout => ({
								...payout,
								affiliate_id: affiliate.id,
								offer: offer.title,
								offer_id: offer.id,
								status: payout.status
							}))
						);
					})
					.filter(Boolean);

				return {
					...affiliate,
					offers: userOffers,
					individualTerms: flatten(userOfferTerms)
				};
			}
			return affiliate;
		}),
		selectOptions: {
			...state.selectOptions,
			offers: [].concat(
				state.selectOptions.offers,
				createSelectOptionsFromSysEnums(action.payload[0].data.map(off => ({ id: off.id, text: off.title })))
			)
		}
	}),

	[ADD_OFFER_SUCCESS]: (state, action) => ({
		...state,
		listOfActiveAffiliatesPanIndex: [],
		listOfActiveOffersPanIndex: {},
		listOfAddedOffersPanIndex: [],
		shownTargetPixelHeightsMap: {},
		nullifiedBoosts: []
	}),

	[NULLIFY_BOOST]: (state, action) => ({
		...state,
		// concat if not already in state
		nullifiedBoosts: find(state.nullifiedBoosts, {
			offerId: action.payload.offerId,
			affiliateId: action.payload.affiliateId,
			locationId: action.payload.locationId
		})
			? state.nullifiedBoosts
			: state.nullifiedBoosts.concat(action.payload)
	}),

	[APPLY]: (state, action) => ({
		...state,
		query: action.payload
	}),

	[RESET_QUERY]: (state, action) => ({
		...state,
		query: initialState.query
	}),

	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, progressBar: !state.progressBar }),

	[COPY_TERMS_SUCCESS]: (state, action) => ({
		...state,
		listOfActiveAffiliatesPanIndex: [],
		listOfActiveOffersPanIndex: {},
		listOfAddedOffersPanIndex: [],
		shownTargetPixelHeightsMap: {}
	}),
	[COPY_TERMS_ERROR]: (state, action) => ({ ...state, error: true }),
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true })
};

export default handleActions(reducers, cloneDeep(initialState));

export const selectors = {
	getData: state => state.affiliates.data,
	getFormData: state => {
		const {
			data,
			listOfActiveAffiliatesPanIndex,
			listOfActiveOffersPanIndex,
			listOfAddedOffersPanIndex,
			shouldShowAddAffiliateSelect,
			shownTargetPixelHeightsMap,
			selectOptions,
			searchFormInitialValue,
			masterOffers,
			nullifiedBoosts
		} = state.affiliates;

		const childAffiliates = data;

		const mappedAffiliates = childAffiliates
			? childAffiliates.map(a => {
				let offers = a.offers || [];
				offers = offers.filter(o => !!find(masterOffers, { id: o.id }));
				const offerMasterTargets = masterOffers
					? flatten(masterOffers.map(o => o.payouts && o.payouts.map(p => ({ ...p, offer_id: o.id }))).filter(Boolean))
					: [];
				const indTerms = a.individualTerms || [];
				const preparedOffers = getPreparedSavedOffers(offers, indTerms, offerMasterTargets);

				return {
					affiliate: {
						value: a.id,
						label: a.email,
						image: a.avatar,
						rubRevenue: a.rubRevenue,
						usdRevenue: a.usdRevenue
					},
					offer_ids: a.offer_ids.filter(Boolean).filter(id => !!find(masterOffers, { id })),
					offers: preparedOffers
				};
			})
			: [];

		const initialValues = { child_affiliates: mappedAffiliates };

		return {
			initialValues,
			listOfActiveAffiliatesPanIndex,
			listOfActiveOffersPanIndex,
			listOfAddedOffersPanIndex,
			shouldShowAddAffiliateSelect,
			shownTargetPixelHeightsMap,
			searchFormInitialValue: { search: searchFormInitialValue },
			selectOptions,
			masterOffers,
			nullifiedBoosts
		};
	},
	getAffiliatesTotal: state => state.affiliates.affiliatesTotal,
	getPayoutCurrencies: state => state.app.lists['payout-currencies'],
	getIntl: state => intl(state),
	getProgressBar: state => state.affiliates.progressBar,
	getError: state => state.affiliates.error
};

// functions

const getPreparedSavedOffers = (offers, individualTerms, offerMasterTargets) =>
	offers.map(offer => {
		const groupTermsByOffer = groupBy(individualTerms, term => term.offer_id);
		const groupMasterTermsByOffer = groupBy(offerMasterTargets, term => term.offer_id);
		const targets = getTargetValues(groupTermsByOffer[offer.id]) || [];
		const masterTargets = getTargetValues(groupMasterTermsByOffer[offer.id]) || [];

		return {
			hasIndividualTerms: offer.payouts && offer.payouts[0] ? !!offer.payouts[0]['is_individual_term'] : false,
			offer: {
				value: offer.id,
				label: offer.title,
				image: offer.image,
				revenue: offer.revenue
			},
			targets: targets
				.map(target => {
					let masterTarget = find(masterTargets, {
						location: target.location,
						offer_id: target.offer_id
					});

					return masterTarget && { ...target, masterTarget };
				})
				.filter(Boolean)
		};
	});

const prepareValidationError = (formData, intl) => {
	const validationsErrors = [];

	formData['child_affiliates'].forEach((affiliate, index) => {
		validationsErrors[index] = {
			offers: {}
		};

		affiliate['offers'].forEach((field, offersIndex) => {
			const validationsTargetsError = [];

			field['targets'].forEach((targetsField, targetsIndex) => {
				validationsTargetsError[targetsIndex] = {};
				if ((!targetsField.rub && !targetsField.usd) || (targetsField.rub == 0 || targetsField.usd == 0)) {
					validationsTargetsError[targetsIndex]['rub'] = intl('должно быть >= 0.01');
					validationsTargetsError[targetsIndex]['usd'] = intl('должно быть >= 0.01');
				} else {
					if (targetsField.rub && isNaN(parseFloat(targetsField.rub))) {
						validationsTargetsError[targetsIndex]['rub'] = intl('должно быть числом');
					}
					if (targetsField.usd && isNaN(parseFloat(targetsField.usd))) {
						validationsTargetsError[targetsIndex]['usd'] = intl('должно быть числом');
					}
				}
			});

			validationsErrors[index]['offers'][offersIndex] = {};
			validationsErrors[index]['offers'][offersIndex]['targets'] = validationsTargetsError;
		});
	});

	return validationsErrors;
};

const getTargetValues = targeting => {
	const fields = [];

	forEach(groupBy(targeting, 'location_id'), groupedFields => {
		let mergedField = {};
		forEach(groupedFields, field => {
			mergedField = {
				...mergedField,
				...field,
				location_id: {
					label: field.location,
					value: field.location_id
				},
				[field.currency]: field.amount,
				affiliate_ids: field.affiliate_ids ? field.affiliate_ids.map(obj => ({ value: obj.id, label: obj.text })) : null
			};
		});
		fields.push(mergedField);
	});

	return fields;
};

const requestData = (dispatch, { shouldShowProgressBar = false, shouldAppendData = false }, query = {}) => {
	const initialQuery = initialState.query;
	const offset = query.offset || initialQuery.offset;
	const limit = query.limit || initialQuery.limit;
	const urls = [`${endpoints.MASTER_ACCOUNT.BASE}/full?${qs.stringify({ ...query, offset, limit })}`];

	// first request. later in pagination fetches we dont need to refetch master offers
	if (!shouldAppendData) {
		urls.push(endpoints.OFFERS.LIST);
	}

	return http({
		url: urls,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
		addToResponse: {
			shouldShowProgressBar,
			shouldAppendData
		}
	})(dispatch);
};



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/affiliates/index.js